/**
 * Single source of truth for locale / timezone / date-cast resolution.
 *
 * Intentionally bridges Pinia stores and i18n so that value objects
 * ({@link Moment}, {@link ActivixDate}) don't need to import stores directly.
 */
import { get } from 'lodash-es';
import { enUS, fr } from 'date-fns/locale';
import { useUserStore } from '@/store/modules/user/store.js';
import { useGlobalStore } from '@/store/store.ts';
import i18n from '@/plugins/vue-i18n.js';

export type SupportedDateLocale = 'en' | 'fr';
export type SupportedDateType = 'date' | 'time' | 'dateTime';

const DEFAULT_LOCALE: SupportedDateLocale = 'en';
export const DEFAULT_TIMEZONE = 'America/Montreal';
const DATE_FNS_LOCALES = {
    en: enUS,
    fr,
} as const;

/** Normalizes any locale string to 'en' or 'fr'. Falls back to the active locale, then 'en'. */
export const getCurrentLocale = (locale = ''): SupportedDateLocale => {
    const normalizedLocale = String(locale || i18n.getLocale() || DEFAULT_LOCALE)
        .trim()
        .toLowerCase();

    return normalizedLocale.startsWith('fr') ? 'fr' : 'en';
};

/** Returns `true` when the resolved locale is English. */
export const isEnglishLocale = (locale = ''): boolean => {
    return getCurrentLocale(locale) === 'en';
};

/** Returns the authenticated user's timezone, defaulting to America/Montreal. */
export const getCurrentTimezone = (): string => {
    return useUserStore().parentAuthUser?.timezone || DEFAULT_TIMEZONE;
};

/** Returns the date-fns locale object matching the resolved locale. */
export const getDateFnsLocale = (locale = '') => {
    return DATE_FNS_LOCALES[getCurrentLocale(locale)];
};

/** Looks up the date cast ('date' | 'time' | 'dateTime') for a model field key. */
export const resolveDateTypeForKey = (key = ''): SupportedDateType => {
    switch (get(useGlobalStore().configs.dateCasts, key)) {
        case 'date':
            return 'date';

        case 'time':
            return 'time';

        default:
            return 'dateTime';
    }
};
